import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { AttentionItem, LeadershipExceptions } from '../lib/types';
import {
  CADENCE_LABEL,
  CADENCE_TONE,
  IMPORTANCE_TONE,
  formatDate,
  formatDateTime,
  overdueLabel,
  titleCase,
} from '../lib/format';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorNotice,
  Loading,
  PageHeader,
  SectionNote,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';
import { WorkItemDialog } from '../features/WorkItemDialog';

/**
 * §24 Leadership Home — exception-first.
 *
 * Answers §69's leadership question: *where are we failing to execute, and where
 * should Customer Success be doing more?* Nothing here is a list of accounts;
 * everything is a list of things that are not happening. The sections are §24's
 * own three: Execution Gaps, Engagement Gaps and Do More.
 *
 * Where a §24 item depends on a later phase it is stated as unavailable rather
 * than quietly omitted — an empty section should not be mistaken for good news.
 */
export function LeadershipPage() {
  const [selected, setSelected] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['leadership'],
    queryFn: () => api.get<LeadershipExceptions>('/cs/leadership/exceptions'),
  });

  const data = query.data;

  return (
    <>
      <PageHeader
        title="Leadership"
        description="Where we are failing to do what we said we would do, and where we should be doing more."
      />

      {query.isLoading && <Loading label="Assembling exceptions" />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {data && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            <StatTile
              label="Overdue commitments"
              value={data.counts.overdueCommitments}
              tone="critical"
            />
            <StatTile label="At risk" value={data.counts.commitmentsAtRisk} tone="serious" />
            <StatTile label="Escalated" value={data.counts.escalated} tone="critical" />
            <StatTile
              label="Readiness unconfirmed"
              value={data.counts.readinessChecksDue}
              tone="warning"
            />
            <StatTile
              label="Overdue follow-ups"
              value={data.counts.overdueFollowups}
              tone="serious"
            />
            <StatTile
              label="Accounts behind cadence"
              value={data.counts.accountsBehindCadence}
              tone="warning"
              context={`${data.counts.strategicAccountsBehindCadence} strategic`}
            />
            <StatTile
              label="Expansion signals"
              value={data.counts.expansionSignals}
              tone="info"
            />
          </div>

          {/* --- Execution gaps --- */}
          <section>
            <h2 className="mb-3 text-sm font-semibold tracking-tight text-ink">Execution gaps</h2>
            <div className="space-y-4">
              <Card padded={false}>
                <div className="px-4 pt-4">
                  <CardHeader
                    title="Escalated to leadership"
                    count={data.executionGaps.escalatedToLeadership.length}
                    description="Past the escalation threshold for the account's importance, or blocked, critical or customer-escalated."
                  />
                </div>
                {data.executionGaps.escalatedToLeadership.length === 0 ? (
                  <div className="px-4 pb-4">
                    <EmptyState title="Nothing escalated" />
                  </div>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>Commitment</Th>
                        <Th>Account</Th>
                        <Th>Importance</Th>
                        <Th>Customer date</Th>
                        <Th align="right">Escalated</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.executionGaps.escalatedToLeadership.map((row) => (
                        <Tr key={row.workItemId} onClick={() => setSelected(row.workItemId)}>
                          <Td className="font-medium">{row.title}</Td>
                          <Td>
                            <Link
                              to={`/accounts/${row.accountId}`}
                              onClick={(event) => event.stopPropagation()}
                              className="text-accent hover:underline"
                            >
                              {row.accountName}
                            </Link>
                          </Td>
                          <Td>
                            <Badge
                              tone={IMPORTANCE_TONE[row.accountImportance]}
                              dot={row.accountImportance === 'strategic'}
                            >
                              {titleCase(row.accountImportance)}
                            </Badge>
                          </Td>
                          <Td className="text-critical">{formatDate(row.customerDueAt)}</Td>
                          <Td align="right" className="text-xs whitespace-nowrap text-muted">
                            {formatDate(row.escalatedAt)}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card>

              <ExceptionList
                title="Overdue commitments"
                description="Past the customer date and not complete."
                items={data.executionGaps.overdueCommitments}
                onSelect={setSelected}
              />
              <ExceptionList
                title="Commitments at risk"
                description="Blocked by a dependency, or past the internal target with the customer date still ahead."
                items={data.executionGaps.commitmentsAtRisk}
                onSelect={setSelected}
              />
              <ExceptionList
                title="Due soon without confirmed readiness"
                description="The internal readiness check has arrived and nobody has confirmed the commitment will land."
                items={data.executionGaps.dueSoonWithoutReadiness}
                onSelect={setSelected}
              />
              <ExceptionList
                title="Overdue follow-ups"
                description="We are waiting on the customer and the follow-up date has passed."
                items={data.executionGaps.overdueFollowups}
                onSelect={setSelected}
              />
              <ExceptionList
                title="Customer responses awaiting review"
                description="Automation spotted a reply that may close a follow-up. Nothing closes without a person."
                items={data.executionGaps.responsesAwaitingReview}
                onSelect={setSelected}
              />

              <Card padded={false}>
                <div className="px-4 pt-4">
                  <CardHeader
                    title="Projects with no meaningful activity"
                    count={data.executionGaps.projectsWithoutActivity.length}
                    description="Open projects untouched for three weeks or more."
                  />
                </div>
                {data.executionGaps.projectsWithoutActivity.length === 0 ? (
                  <div className="px-4 pb-4">
                    <EmptyState title="Every open project has moved recently" />
                  </div>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>Project</Th>
                        <Th>Account</Th>
                        <Th>Status</Th>
                        <Th>Owner</Th>
                        <Th align="right">Last touched</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.executionGaps.projectsWithoutActivity.map((project) => (
                        <Tr key={project.projectId}>
                          <Td className="font-medium">
                            <Link
                              to={`/projects/${project.projectId}`}
                              className="text-accent hover:underline"
                            >
                              {project.projectName}
                            </Link>
                          </Td>
                          <Td>
                            <Link
                              to={`/accounts/${project.accountId}`}
                              className="text-accent hover:underline"
                            >
                              {project.accountName}
                            </Link>
                          </Td>
                          <Td className="text-ink-2">{titleCase(project.status)}</Td>
                          <Td className="text-xs text-ink-2">{project.ownerName}</Td>
                          <Td align="right" className="text-xs whitespace-nowrap text-muted">
                            {formatDate(project.lastTouchedAt)}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card>
            </div>
          </section>

          {/* --- Engagement gaps --- */}
          <section>
            <h2 className="mb-1 text-sm font-semibold tracking-tight text-ink">Engagement gaps</h2>
            <p className="mb-3 text-xs text-ink-2">
              An engagement gap is a fact about our contact rhythm. It is not automatically account
              risk.
            </p>
            <Card padded={false}>
              <Table>
                <thead>
                  <tr>
                    <Th>Account</Th>
                    <Th>Importance</Th>
                    <Th>Cadence</Th>
                    <Th>Gaps</Th>
                    <Th>Primary CSM</Th>
                    <Th align="right">Last activity</Th>
                    <Th align="right">Next meeting</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.engagementGaps.accounts
                    .filter((account) => account.cadence.status !== 'on_cadence')
                    .map((account) => (
                      <Tr key={account.accountId}>
                        <Td>
                          <Link
                            to={`/accounts/${account.accountId}`}
                            className="font-medium text-accent hover:underline"
                          >
                            {account.accountName}
                          </Link>
                          {!account.hasSuccessPlan && (
                            <span className="mt-0.5 block text-xs text-muted">
                              No success plan — nothing to measure against
                            </span>
                          )}
                        </Td>
                        <Td>
                          <Badge
                            tone={IMPORTANCE_TONE[account.accountImportance]}
                            dot={account.accountImportance === 'strategic'}
                          >
                            {titleCase(account.accountImportance)}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge tone={CADENCE_TONE[account.cadence.status]}>
                            {CADENCE_LABEL[account.cadence.status]}
                          </Badge>
                        </Td>
                        <Td className="max-w-80">
                          <ul className="space-y-0.5 text-xs text-ink-2">
                            {account.cadence.gaps.map((gap) => (
                              <li key={gap.kind}>{gap.summary}</li>
                            ))}
                          </ul>
                        </Td>
                        <Td className="text-xs text-ink-2">{account.primaryCsmName}</Td>
                        <Td align="right" className="tnum text-xs whitespace-nowrap text-muted">
                          {account.cadence.daysSinceLastActivity === null
                            ? '—'
                            : `${account.cadence.daysSinceLastActivity}d ago`}
                        </Td>
                        <Td align="right" className="tnum text-xs whitespace-nowrap text-muted">
                          {account.cadence.daysUntilNextMeeting === null
                            ? 'none'
                            : `in ${account.cadence.daysUntilNextMeeting}d`}
                        </Td>
                      </Tr>
                    ))}
                </tbody>
              </Table>
              {data.engagementGaps.accounts.every((a) => a.cadence.status === 'on_cadence') && (
                <div className="p-4">
                  <EmptyState title="Every account is on cadence" />
                </div>
              )}
            </Card>
          </section>

          {/* --- Contact coverage (Design 0001) --- */}
          <section>
            <h2 className="mb-1 text-sm font-semibold tracking-tight text-ink">Contact coverage</h2>
            <p className="mb-3 text-xs text-ink-2">
              Relationships that are thin or lapsing: a sponsor who has left, nobody senior to talk to, or
              key people we have not reached in far too long.
            </p>
            <Card padded={false}>
              <Table>
                <thead>
                  <tr>
                    <Th>Account</Th>
                    <Th>Importance</Th>
                    <Th>Coverage</Th>
                    <Th>People overdue</Th>
                    <Th>Primary CSM</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.contactGaps.accounts.map((account) => (
                    <Tr key={account.accountId}>
                      <Td>
                        <Link to={`/accounts/${account.accountId}`} className="font-medium text-accent hover:underline">
                          {account.accountName}
                        </Link>
                      </Td>
                      <Td>
                        <Badge tone={IMPORTANCE_TONE[account.accountImportance]} dot={account.accountImportance === 'strategic'}>
                          {titleCase(account.accountImportance)}
                        </Badge>
                      </Td>
                      <Td className="max-w-80">
                        <ul className="space-y-0.5 text-xs text-ink-2">
                          {account.coverage.map((gap, index) => (
                            <li key={`${gap.kind}-${index}`}>
                              {gap.severity === 'high' && <span className="font-medium text-critical">High · </span>}
                              {gap.summary}
                            </li>
                          ))}
                          {account.coverage.length === 0 && <li className="text-muted">Covered</li>}
                        </ul>
                      </Td>
                      <Td className="max-w-80">
                        <ul className="space-y-0.5 text-xs text-ink-2">
                          {account.cadence.map((gap) => (
                            <li key={gap.contactId}>
                              <span className="font-medium text-ink">{gap.contactName}</span>: {gap.overdueByDays}d overdue
                            </li>
                          ))}
                          {account.overdueContactCount > account.cadence.length && (
                            <li className="text-muted">and {account.overdueContactCount - account.cadence.length} more</li>
                          )}
                          {account.cadence.length === 0 && <li className="text-muted">None</li>}
                        </ul>
                      </Td>
                      <Td className="text-xs text-ink-2">{account.primaryCsmName}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
              {data.contactGaps.accounts.length === 0 && (
                <div className="p-4">
                  <EmptyState title="Every account is covered and in touch" />
                </div>
              )}
            </Card>
          </section>

          {/* --- Do more --- */}
          <section>
            <h2 className="mb-3 text-sm font-semibold tracking-tight text-ink">Do more</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card padded={false}>
                <div className="px-4 pt-4">
                  <CardHeader
                    title="Expansion signals"
                    count={data.doMore.expansionSignals.length}
                    description="Something a customer said that is worth pursuing. Not a CRM forecast."
                  />
                </div>
                {data.doMore.expansionSignals.length === 0 ? (
                  <div className="px-4 pb-4">
                    <EmptyState title="No open signals" />
                  </div>
                ) : (
                  <ul className="divide-y divide-border border-t border-border">
                    {data.doMore.expansionSignals.map((signal) => (
                      <li key={signal.workItemId} className="px-4 py-2.5">
                        <p className="text-sm text-ink">{signal.title}</p>
                        <p className="mt-0.5 text-xs text-ink-2">
                          <Link
                            to={`/accounts/${signal.accountId}`}
                            className="text-accent hover:underline"
                          >
                            {signal.accountName}
                          </Link>
                          {' · '}
                          {titleCase(signal.signalType)}
                          {signal.potentialValueText && ` · ${signal.potentialValueText}`}
                          {signal.targetPeriod && ` · ${signal.targetPeriod}`}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card padded={false}>
                <div className="px-4 pt-4">
                  <CardHeader
                    title="Customers to notify"
                    count={data.doMore.pendingCustomerNotifications.length}
                    description="A feature this account asked for has shipped and they have not been told."
                  />
                </div>
                {data.doMore.pendingCustomerNotifications.length === 0 ? (
                  <div className="px-4 pb-4">
                    <EmptyState title="Every shipped request has been communicated" />
                  </div>
                ) : (
                  <ul className="divide-y divide-border border-t border-border">
                    {data.doMore.pendingCustomerNotifications.map((item) => (
                      <li key={item.workItemId} className="px-4 py-2.5">
                        <button
                          className="w-full text-left"
                          onClick={() => item.workItemId && setSelected(item.workItemId)}
                        >
                          <p className="text-sm text-ink">{item.title}</p>
                          <p className="mt-0.5 text-xs text-ink-2">
                            {item.accountName} · {overdueLabel(item.businessDaysOverdue)}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card>
                <CardHeader
                  title="Business reviews due"
                  count={data.doMore.businessReviewsDue.length}
                />
                {data.doMore.businessReviewsDue.length === 0 ? (
                  <EmptyState title="No business reviews overdue" />
                ) : (
                  <ul className="divide-y divide-border">
                    {data.doMore.businessReviewsDue.map((row) => (
                      <li key={row.accountId} className="py-2">
                        <Link
                          to={`/accounts/${row.accountId}`}
                          className="text-sm text-accent hover:underline"
                        >
                          {row.accountName}
                        </Link>
                        <p className="mt-0.5 text-xs text-ink-2">{row.gap?.summary}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card>
                <CardHeader
                  title="Not measured yet"
                  description="§24 items that depend on modules still to be built."
                />
                <ul className="space-y-2">
                  {data.doMore.unavailable.map((entry) => (
                    <li key={entry.item}>
                      <p className="text-sm text-ink-2">{entry.item}</p>
                      <p className="text-xs text-muted">{entry.reason}</p>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </section>

          <SectionNote>
            Generated {formatDateTime(data.generatedAt)}. Response-SLA breaches join Execution Gaps
            once email ingestion is connected (Phase 4); the Weekly CS Review (§25) assembles this
            same set every Monday from Phase 6.
          </SectionNote>
        </div>
      )}

      {selected && (
        <WorkItemDialog
          workItemId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => query.refetch()}
        />
      )}
    </>
  );
}

function ExceptionList({
  title,
  description,
  items,
  onSelect,
}: {
  title: string;
  description: string;
  items: AttentionItem[];
  onSelect: (id: string) => void;
}) {
  return (
    <Card padded={false}>
      <div className="px-4 pt-4">
        <CardHeader title={title} count={items.length} description={description} />
      </div>
      {items.length === 0 ? (
        <div className="px-4 pb-4">
          <EmptyState title="Nothing here" />
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Item</Th>
              <Th>Account</Th>
              <Th>Why</Th>
              <Th align="right">Date</Th>
              <Th align="right">Status</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <Tr
                key={`${item.kind}-${item.workItemId}`}
                onClick={() => item.workItemId && onSelect(item.workItemId)}
              >
                <Td className="font-medium">{item.title}</Td>
                <Td>
                  <span className="flex items-center gap-2">
                    <Link
                      to={`/accounts/${item.accountId}`}
                      onClick={(event) => event.stopPropagation()}
                      className="text-accent hover:underline"
                    >
                      {item.accountName}
                    </Link>
                    {item.accountImportance === 'strategic' && (
                      <Badge tone="info" dot={false}>
                        Strategic
                      </Badge>
                    )}
                  </span>
                </Td>
                <Td className="max-w-80 text-xs text-ink-2">{item.reason}</Td>
                <Td align="right" className="text-xs whitespace-nowrap text-muted">
                  {formatDate(item.dueAt)}
                </Td>
                <Td align="right">
                  <Badge tone={(item.businessDaysOverdue ?? 0) > 0 ? 'critical' : 'neutral'}>
                    {overdueLabel(item.businessDaysOverdue)}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
